import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessage,
    SettingState,
    TibboDataType,
    SettingOption,
    ValidationMessageType,
} from '@appblocks/codegeneration/types';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';

export default class ModbusSlave extends BaseFeature {
    constructor() {
        super();
        this.name = 'Modbus Slave';
        this.id = 'modbus_slave';
        this.icon = 'BsHddNetwork';
        this.requirements = ['tios'];

        const settings = [
            {
                id: 'DEBUG_PRINT',
                name: 'Debug Printing',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'ON',
                        value: '1',
                    },
                    {
                        name: 'OFF',
                        value: '0',
                    },
                ],
            },
            {
                id: 'SLAVEPROTOCOL',
                name: 'Connection Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'modbus_RTU',
                options: [
                    {
                        name: 'TCP',
                        value: 'modbus_TCP',
                    },
                    {
                        name: 'Serial',
                        value: 'modbus_RTU',
                    },
                ],
            },
            {
                id: 'DEVICEID',
                name: 'Device ID',
                settingType: SettingType.NUMBER,
                dataType: TibboDataType.SETTING,
                max: 254,
                required: true,
                default: '1',
                setting: {
                    SETTINGID: 'DEVICEID',
                    NAME: 'Device ID',
                    SETTINGTYPE: 'float',
                    DATATYPE: 'F',
                    MAX: 254,
                    DEFAULT: '1',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
            },
            {
                id: 'SLAVE_SERIAL_PORT',
                name: 'Slave Serial Port',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
            },
            {
                id: 'SLAVE_TCP_PORT',
                name: 'Slave TCP Port',
                settingType: SettingType.STRING,
                required: true,
                default: '502',
            },
            {
                id: 'MODBUSTABLEITEMS',
                name: 'Slave Registers',
                settingType: SettingType.ARRAY,
                refId: 'modbus_table_item',
                required: true,
                default: [] as BaseFeature[],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getEnabled(): boolean {
        const MODBUSTABLEITEMS = this.getSettingValue('MODBUSTABLEITEMS');
        if (MODBUSTABLEITEMS.length > 0) {
            return true;
        }
        return false;
    }

    getDependencies() {
        const dependencies = [
            'modbus',
        ];

        return dependencies;
    }

    getDefines(): Array<Define> {
        const defines: Array<Define> = [];
        if (this.getSettingValue('DEBUG_PRINT').toString() === '1') {
            defines.push({
                name: 'MODBUS_DEBUG_PRINT',
                value: '1',
            });
        }
        defines.push({
            name: 'MODBUS_SLAVE_ENABLED',
            value: '1',
        });
        return defines;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const project: ProjectConfiguration = this.project;
        switch (settingId) {
            case 'SLAVE_SERIAL_PORT':
                return project.getModbusSerialPorts();
            default:
                return super.getSettingOptions(settingId);
        }
    }

    getState(settingId: string): SettingState {
        switch (settingId) {
            case 'DEVICEID':
                return this.getSettingValue('SLAVEPROTOCOL') === 'modbus_TCP' ? SettingState.HIDDEN : SettingState.ENABLED;
            case 'SLAVE_SERIAL_PORT':
                return this.getSettingValue('SLAVEPROTOCOL') === 'modbus_TCP' ? SettingState.HIDDEN : SettingState.ENABLED;
            case 'SLAVE_TCP_PORT':
                return this.getSettingValue('SLAVEPROTOCOL') === 'modbus_TCP' ? SettingState.ENABLED : SettingState.HIDDEN;

            default:
                return SettingState.ENABLED;
        }
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const protocol = this.getSettingValue('SLAVEPROTOCOL');
        const serialPort = this.getSettingValue('SLAVE_SERIAL_PORT');
        const registers = this.getSettingValue('MODBUSTABLEITEMS');
        const registersHashmap = new Map();
        if (protocol === 'modbus_RTU' && serialPort === '') {
            errors.push({
                message: 'Serial port must be set',
                messageType: ValidationMessageType.ERROR,
                featureId: this.getID(),
                settingId: 'SLAVE_SERIAL_PORT',
            });
        }

        for (let i = 0; i < registers.length; i++) {
            const register = registers[i];
            const address = register.getSettingValue('ADDRESS');
            const registersAtAddress = registersHashmap.get(address);
            if (registersAtAddress) {
                registersAtAddress.push(register);
                registersHashmap.set(address, registersAtAddress);
            } else {
                registersHashmap.set(address, [register]);
            }
        }

        registersHashmap.forEach((value) => {
            if (value.length > 1) {
                for (let i = 0; i < value.length; i++) {
                    errors.push({
                        message: 'Register addresses must be unique',
                        messageType: ValidationMessageType.ERROR,
                        featureId: value[i].getID(),
                        settingId: 'ADDRESS',
                    });
                }
            }
        });
        return errors;
    }

    getCodeBlocks() {
        let blocks: Array<CodeBlock> = [];
        const MODBUSTABLEITEMS = this.getSettingValue('MODBUSTABLEITEMS');
        if (!this.getEnabled()) {
            return blocks;
        }
        if (this.project.runtime === 'simulator') {
            return blocks;
        }
        const DEVICEID = this.getSettingValue('DEVICEID');
        let SLAVE_SERIAL_PORT;
        const serialPortFeature = this.project.getFeature(this.getSettingValue('SLAVE_SERIAL_PORT'));
        if (serialPortFeature) {
            SLAVE_SERIAL_PORT = `SER_${serialPortFeature.getSettingValue('NAME')}`;
        }
        let callback_modbus_slave_reply = '';
        let setupCode = '';

        for (let i = 0; i < MODBUSTABLEITEMS.length; i += 1) {
            const codeBlocks = MODBUSTABLEITEMS[i].getCodeBlocks();
            for (let j = 0; j < codeBlocks.length; j += 1) {
                const block = codeBlocks[j];
                switch (block.location) {
                    case 'modbus_callback_ReadCoilStatus':
                    case 'modbus_callback_ReadInputStatus':
                    case 'modbus_callback_WriteSingleCoil':
                    case 'modbus_callback_ReadHoldingRegister':
                    case 'modbus_callback_ReadInputRegister':
                    case 'modbus_callback_set_holding_register':
                        blocks.push(block);
                        break;
                    default:
                        blocks = blocks.concat(block);
                        break;
                }
                if (block.location === 'callback_modbus_slave_reply') {
                    callback_modbus_slave_reply += block.template;
                } else {
                    //
                }
            }
        }


        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
modbus_slave_id=${DEVICEID}`,
        });

        if (this.getSettingValue('SLAVEPROTOCOL') === 'modbus_TCP') {
            setupCode += `
    modbus_slave_protocol=modbus_TCP
    modbus_slave_interface=sock_get("MBS")
    sock.num=modbus_slave_interface
    sock.protocol=PL_SOCK_PROTOCOL_TCP
    sock.localportlist="${this.getSettingValue('SLAVE_TCP_PORT')}"
    sock.inconmode=PL_SOCK_INCONMODE_ANY_IP_ANY_PORT
    sock.reconmode=PL_SOCK_RECONMODE_3
    sock.allowedinterfaces="NET,WLN,PPP"
    sock.txbuffrq(1)
    sock.rxbuffrq(1)
    sys.buffalloc()
    `;
            blocks.push({
                location: 'on_sock_data_arrival',
                blockType: BlockType.CODE,
                template: `
    if sock.num=modbus_slave_interface AND modbus_slave_protocol=modbus_TCP then
        'modbus slave data
        modbus_slave_receive(sock.getdata(255),modbus_slave_interface,modbus_TCP)
    end if`,
            });

            blocks.push({
                location: 'on_sock_event',
                blockType: BlockType.CODE,
                template: `
    if sock.num=modbus_slave_interface AND modbus_slave_protocol=modbus_TCP then
        if newstatesimple=PL_SSTS_EST then
            sys.debugprint("modbus slave received connection\\r\\n")
        end if
    end if`,
            });
        } else {
            blocks.push({
                location: 'on_ser_data_arrival',
                blockType: BlockType.CODE,
                template: `
    if ser.num=modbus_slave_interface AND modbus_slave_protocol=modbus_RTU then
        'modbus slave data
        modbus_slave_receive(ser.getdata(255),modbus_slave_interface,modbus_RTU)
    end if`,
            });
            setupCode += `
    modbus_slave_interface=${SLAVE_SERIAL_PORT}
    modbus_slave_protocol=modbus_RTU
    `;
        }

        blocks.push({
            location: 'on_ser_data_sent',
            blockType: BlockType.CODE,
            id: 'modbus_proc_ser_data_sent',
            template: `
    modbus_proc_ser_data_sent()`,
        });

        blocks.push({
            location: 'modbus_setup',
            blockType: BlockType.CODE,
            template: setupCode,
        });

        return blocks;
    }

    getDetails(): string {
        return `
## Modbus Slave
The device can act as a Modbus TCP or RTU slave device.

Define registers for the device, and **variables** will be created for each register.

To update a register value, update the connected **variable**.

The device can also trigger custom functions when a register is written to with the **On Modbus Register Written** block.
        `;
    }
}
